import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Mail, Edit, Trash2, Shield, Settings2, Key, User, Building, Bell, Palette, Lock, Save, Eye, EyeOff, RotateCcw, List, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StorageStats from "@/components/StorageStats";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dropdown configuration for each module
const DROPDOWN_MODULES = {
  leads: {
    label: "Leads",
    fields: {
      stage: { label: "Stage", description: "Lead progression stages" },
      category: { label: "Category", description: "Business categories" },
      source: { label: "Source", description: "Lead sources" }
    }
  },
  projects: {
    label: "Projects",
    fields: {
      status: { label: "Status", description: "Project statuses" },
      type: { label: "Type", description: "Project types" },
      execution_model: { label: "Execution Model", description: "Delivery models" }
    }
  },
  tasks: {
    label: "Tasks",
    fields: {
      status: { label: "Status", description: "Task statuses" },
      priority: { label: "Priority", description: "Priority levels" }
    }
  },
  expenses: {
    label: "Expenses",
    fields: {
      status: { label: "Status", description: "Expense statuses" },
      payment_mode: { label: "Payment Mode", description: "Payment methods" }
    }
  },
  hr: {
    label: "HR & Team",
    fields: {
      department: { label: "Department", description: "Employee departments" },
      employment_type: { label: "Employment Type", description: "Types of employment" },
      leave_type: { label: "Leave Type", description: "Types of leave" }
    }
  }
};

// Color options for badges
const COLOR_OPTIONS = [
  { value: "bg-slate-100 text-slate-700", label: "Gray" },
  { value: "bg-blue-100 text-blue-700", label: "Blue" },
  { value: "bg-emerald-100 text-emerald-700", label: "Green" },
  { value: "bg-amber-100 text-amber-700", label: "Amber" },
  { value: "bg-red-100 text-red-700", label: "Red" },
  { value: "bg-purple-100 text-purple-700", label: "Purple" },
  { value: "bg-pink-100 text-pink-700", label: "Pink" },
  { value: "bg-cyan-100 text-cyan-700", label: "Cyan" },
  { value: "bg-orange-100 text-orange-700", label: "Orange" }
];

const AVAILABLE_MODULES = [
  { value: "dashboard", label: "Dashboard" },
  { value: "leads", label: "Leads" },
  { value: "projects", label: "Projects" },
  { value: "finance", label: "Finance" },
  { value: "tasks", label: "Tasks" },
  { value: "team", label: "Team & HR" },
  { value: "vendors", label: "Vendors" },
  { value: "analytics", label: "Analytics" },
  { value: "settings", label: "Settings" },
  { value: "quotation-generator", label: "Quotation Generator" },
];

export default function SettingsPage() {
  const { token, user, updateUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState("users");
  
  // Profile state
  const [profileForm, setProfileForm] = useState({ name: "", email: "" });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Company settings state
  const [companySettings, setCompanySettings] = useState({
    company_name: "Brillen Interiors",
    company_address: "",
    company_phone: "",
    company_email: "",
    company_gst: "",
    company_pan: "",
    bank_name: "",
    bank_account: "",
    bank_ifsc: "",
    invoice_prefix: "INV",
    quotation_prefix: "QTN",
    po_prefix: "PO"
  });
  const [savingCompany, setSavingCompany] = useState(false);

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    lead_alerts: true,
    project_updates: true,
    task_reminders: true,
    payment_alerts: true,
    weekly_reports: false
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    role: "Viewer",
    assigned_modules: [],
  });

  // Dropdown options state
  const [dropdownConfigs, setDropdownConfigs] = useState({});
  const [expandedModules, setExpandedModules] = useState({});
  const [addOptionDialog, setAddOptionDialog] = useState({ open: false, module: null, field: null });
  const [newOption, setNewOption] = useState({ value: "", label: "", color: "bg-slate-100 text-slate-700" });
  const [editOptionDialog, setEditOptionDialog] = useState({ open: false, module: null, field: null, option: null });
  const [editOption, setEditOption] = useState({ value: "", label: "", color: "" });

  const isAdmin = user?.role === "Admin";

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchCompanySettings();
      fetchDropdownConfigs();
    }
    if (user) {
      setProfileForm({ name: user.name || "", email: user.email || "" });
      fetchNotificationSettings();
    }
    setLoading(false);
  }, [token, user]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCompanySettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Settings might not exist yet, use defaults
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setNotificationSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Settings might not exist yet, use defaults
    }
  };

  // Dropdown options functions
  const fetchDropdownConfigs = async () => {
    try {
      const response = await axios.get(`${API}/settings/status-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setDropdownConfigs(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch dropdown configs:", error);
    }
  };

  const handleAddOption = async () => {
    if (!newOption.value.trim() || !newOption.label.trim()) {
      toast.error("Value and Label are required");
      return;
    }
    try {
      await axios.post(
        `${API}/settings/status-config/${addOptionDialog.module}/${addOptionDialog.field}/add`,
        newOption,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Option added successfully");
      setAddOptionDialog({ open: false, module: null, field: null });
      setNewOption({ value: "", label: "", color: "bg-slate-100 text-slate-700" });
      fetchDropdownConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add option");
    }
  };

  const handleEditOption = async () => {
    if (!editOption.label.trim()) {
      toast.error("Label is required");
      return;
    }
    try {
      const { module, field } = editOptionDialog;
      const currentOptions = dropdownConfigs[module]?.[field] || [];
      const updatedOptions = currentOptions.map(opt => 
        opt.value === editOption.value ? { ...opt, label: editOption.label, color: editOption.color } : opt
      );
      await axios.put(
        `${API}/settings/status-config/${module}/${field}`,
        updatedOptions,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Option updated successfully");
      setEditOptionDialog({ open: false, module: null, field: null, option: null });
      fetchDropdownConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update option");
    }
  };

  const handleDeleteOption = async (module, field, value) => {
    if (!window.confirm(`Are you sure you want to delete "${value}"?`)) return;
    try {
      await axios.delete(
        `${API}/settings/status-config/${module}/${field}/${encodeURIComponent(value)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Option deleted successfully");
      fetchDropdownConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete option");
    }
  };

  const handleResetModule = async (module) => {
    if (!window.confirm(`Reset all ${DROPDOWN_MODULES[module].label} options to defaults?`)) return;
    try {
      await axios.post(
        `${API}/settings/status-config/reset/${module}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${DROPDOWN_MODULES[module].label} options reset to defaults`);
      fetchDropdownConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset options");
    }
  };

  const toggleModuleExpansion = (module) => {
    setExpandedModules(prev => ({ ...prev, [module]: !prev[module] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const updateData = {
          name: formData.name,
          role: formData.role,
          assigned_modules: formData.assigned_modules,
          is_active: true,
        };
        await axios.put(`${API}/auth/users/${editing.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("User updated successfully");
      } else {
        await axios.post(`${API}/auth/invite`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("User invited successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/auth/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleResetPassword = async (userId, userName) => {
    const newPassword = window.prompt(`Enter new password for ${userName}:`, "");
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await axios.put(`${API}/auth/users/${userId}/reset-password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password reset successfully for ${userName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await axios.put(`${API}/auth/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully");
      if (updateUser) {
        updateUser({ ...user, name: profileForm.name });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveCompanySettings = async () => {
    setSavingCompany(true);
    try {
      await axios.put(`${API}/settings/company`, companySettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Company settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save company settings");
    } finally {
      setSavingCompany(false);
    }
  };

  const handleSaveNotificationSettings = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(`${API}/settings/notifications`, notificationSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notification settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save notification settings");
    } finally {
      setSavingNotifications(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      name: "",
      password: "",
      role: "Viewer",
      assigned_modules: [],
    });
    setEditing(null);
  };

  const openEdit = (u) => {
    setFormData({
      email: u.email,
      name: u.name,
      password: "",
      role: u.role,
      assigned_modules: u.assigned_modules || [],
    });
    setEditing(u);
    setDialogOpen(true);
  };

  const toggleModule = (moduleValue) => {
    if (formData.assigned_modules.includes(moduleValue)) {
      setFormData({
        ...formData,
        assigned_modules: formData.assigned_modules.filter(m => m !== moduleValue)
      });
    } else {
      setFormData({
        ...formData,
        assigned_modules: [...formData.assigned_modules, moduleValue]
      });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brillien-600"></div></div>;
  }

  return (
    <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Settings</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Manage system preferences and user access</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 dark:bg-slate-800">
          {isAdmin && <TabsTrigger value="users">User Management</TabsTrigger>}
          {isAdmin && <TabsTrigger value="company">Company Settings</TabsTrigger>}
          {isAdmin && <TabsTrigger value="dropdowns" data-testid="dropdown-options-tab">Dropdown Options</TabsTrigger>}
          {isAdmin && <TabsTrigger value="storage" data-testid="storage-tab">Storage</TabsTrigger>}
          <TabsTrigger value="profile">My Profile</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        {/* User Management Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="users">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">User Management</h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Manage user accounts and permissions</p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg h-10 px-4" data-testid="invite-user-btn">
                      <Plus className="h-4 w-4 mr-2" />Invite User
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md dark:bg-slate-800 dark:border-slate-700">
                    <DialogHeader>
                      <DialogTitle>{editing ? "Edit User" : "Invite New User"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <Label>Email *</Label>
                        <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required disabled={!!editing} className="dark:border-slate-400 dark:bg-slate-700" />
                      </div>
                      <div>
                        <Label>Name *</Label>
                        <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="dark:border-slate-400 dark:bg-slate-700" />
                      </div>
                      {!editing && (
                        <div>
                          <Label>Initial Password *</Label>
                          <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required className="dark:border-slate-400 dark:bg-slate-700" />
                        </div>
                      )}
                      <div>
                        <Label>Role *</Label>
                        <Select value={formData.role} onValueChange={(val) => setFormData({...formData, role: val})}>
                          <SelectTrigger className="dark:border-slate-400 dark:bg-slate-700"><SelectValue /></SelectTrigger>
                          <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                            <SelectItem value="Admin">Admin</SelectItem>
                            <SelectItem value="HR Manager">HR Manager</SelectItem>
                            <SelectItem value="Manager">Manager</SelectItem>
                            <SelectItem value="Viewer">Viewer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {formData.role !== "Admin" && formData.role !== "HR Manager" && (
                        <div>
                          <Label className="block mb-2">Assigned Modules</Label>
                          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded dark:border-slate-400">
                            {AVAILABLE_MODULES.map((module) => (
                              <div key={module.value} className="flex items-center gap-2">
                                <Checkbox checked={formData.assigned_modules.includes(module.value)} onCheckedChange={() => toggleModule(module.value)} id={module.value} />
                                <label htmlFor={module.value} className="text-sm cursor-pointer">{module.label}</label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2 pt-2">
                        <Button type="submit" className="flex-1 bg-brillien-600 hover:bg-brillien-700">{editing ? "Update User" : "Send Invite"}</Button>
                        <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Users List as Table */}
              <div className="border rounded-lg overflow-hidden bg-white dark:bg-slate-800">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-slate-700 dark:text-slate-300">User</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Role</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Assigned Modules</th>
                      <th className="text-center p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Status</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {users.map((u) => (
                      <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-brillien-100 flex items-center justify-center flex-shrink-0">
                              <User className="h-4 w-4 text-brillien-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">{u.name}</p>
                              <p className="text-sm text-slate-500">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge className={u.role === "Admin" ? "bg-purple-100 text-purple-700" : u.role === "HR Manager" ? "bg-pink-100 text-pink-700" : u.role === "Manager" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"}>
                            {u.role}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {u.role === "Admin" ? (
                            <span className="text-sm text-slate-500 flex items-center gap-1"><Shield className="h-3 w-3" />Full Access</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {u.assigned_modules?.slice(0, 3).map((mod) => {
                                const moduleInfo = AVAILABLE_MODULES.find(m => m.value === mod);
                                return <Badge key={mod} variant="outline" className="text-xs">{moduleInfo?.label || mod}</Badge>;
                              })}
                              {(u.assigned_modules?.length || 0) > 3 && <Badge variant="outline" className="text-xs">+{u.assigned_modules.length - 3}</Badge>}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <Badge className={u.is_active !== false ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                            {u.is_active !== false ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {u.id !== user.id ? (
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="h-8 w-8 p-0" title="Edit"><Edit className="h-4 w-4 text-slate-600" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleResetPassword(u.id, u.name)} className="h-8 w-8 p-0" title="Reset Password"><Key className="h-4 w-4 text-amber-600" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)} className="h-8 w-8 p-0" title="Delete"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">Current User</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        )}

        {/* Company Settings Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="company">
            <div className="space-y-6 max-w-4xl">
              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Company Settings</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Configure your company details for invoices and documents</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <Card className="p-5 border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                    <Building className="h-4 w-4" />Basic Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Company Name *</Label>
                      <Input value={companySettings.company_name} onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})} className="dark:border-slate-400 dark:bg-slate-700" />
                    </div>
                    <div>
                      <Label>Company Address</Label>
                      <Textarea value={companySettings.company_address} onChange={(e) => setCompanySettings({...companySettings, company_address: e.target.value})} rows={2} className="dark:border-slate-400 dark:bg-slate-700" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Phone</Label>
                        <Input value={companySettings.company_phone} onChange={(e) => setCompanySettings({...companySettings, company_phone: e.target.value})} className="dark:border-slate-400 dark:bg-slate-700" />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input type="email" value={companySettings.company_email} onChange={(e) => setCompanySettings({...companySettings, company_email: e.target.value})} className="dark:border-slate-400 dark:bg-slate-700" />
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Tax Info */}
                <Card className="p-5 border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                    <Shield className="h-4 w-4" />Tax & Legal
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label>GST Number</Label>
                      <Input value={companySettings.company_gst} onChange={(e) => setCompanySettings({...companySettings, company_gst: e.target.value})} placeholder="27AAACT0000A1Z5" className="dark:border-slate-400 dark:bg-slate-700" />
                    </div>
                    <div>
                      <Label>PAN Number</Label>
                      <Input value={companySettings.company_pan} onChange={(e) => setCompanySettings({...companySettings, company_pan: e.target.value})} placeholder="AAACT0000A" className="dark:border-slate-400 dark:bg-slate-700" />
                    </div>
                  </div>
                </Card>

                {/* Bank Info */}
                <Card className="p-5 border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                    <Settings2 className="h-4 w-4" />Bank Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Bank Name</Label>
                      <Input value={companySettings.bank_name} onChange={(e) => setCompanySettings({...companySettings, bank_name: e.target.value})} className="dark:border-slate-400 dark:bg-slate-700" />
                    </div>
                    <div>
                      <Label>Account Number</Label>
                      <Input value={companySettings.bank_account} onChange={(e) => setCompanySettings({...companySettings, bank_account: e.target.value})} className="dark:border-slate-400 dark:bg-slate-700" />
                    </div>
                    <div>
                      <Label>IFSC Code</Label>
                      <Input value={companySettings.bank_ifsc} onChange={(e) => setCompanySettings({...companySettings, bank_ifsc: e.target.value})} className="dark:border-slate-400 dark:bg-slate-700" />
                    </div>
                  </div>
                </Card>

                {/* Document Prefixes */}
                <Card className="p-5 border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                    <Settings2 className="h-4 w-4" />Document Prefixes
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Invoice Prefix</Label>
                      <Input value={companySettings.invoice_prefix} onChange={(e) => setCompanySettings({...companySettings, invoice_prefix: e.target.value})} placeholder="INV" className="dark:border-slate-400 dark:bg-slate-700" />
                    </div>
                    <div>
                      <Label>Quotation Prefix</Label>
                      <Input value={companySettings.quotation_prefix} onChange={(e) => setCompanySettings({...companySettings, quotation_prefix: e.target.value})} placeholder="QTN" className="dark:border-slate-400 dark:bg-slate-700" />
                    </div>
                    <div>
                      <Label>Purchase Order Prefix</Label>
                      <Input value={companySettings.po_prefix} onChange={(e) => setCompanySettings({...companySettings, po_prefix: e.target.value})} placeholder="PO" className="dark:border-slate-400 dark:bg-slate-700" />
                    </div>
                  </div>
                </Card>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveCompanySettings} disabled={savingCompany} className="bg-brillien-600 hover:bg-brillien-700">
                  <Save className="h-4 w-4 mr-2" />{savingCompany ? "Saving..." : "Save Company Settings"}
                </Button>
              </div>
            </div>
          </TabsContent>
        )}

        {/* Dropdown Options Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="dropdowns">
            <div className="space-y-6" data-testid="dropdown-options-content">
              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Dropdown Options</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Configure dropdown values for Leads, Projects, Tasks, and Expenses</p>
              </div>

              <div className="space-y-4">
                {Object.entries(DROPDOWN_MODULES).map(([moduleKey, moduleData]) => (
                  <Card key={moduleKey} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 overflow-hidden">
                    {/* Module Header */}
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50"
                      onClick={() => toggleModuleExpansion(moduleKey)}
                      data-testid={`module-header-${moduleKey}`}
                    >
                      <div className="flex items-center gap-3">
                        {expandedModules[moduleKey] ? (
                          <ChevronDown className="h-5 w-5 text-slate-500" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-slate-500" />
                        )}
                        <div>
                          <h3 className="font-semibold text-slate-900 dark:text-white">{moduleData.label}</h3>
                          <p className="text-sm text-slate-500">{Object.keys(moduleData.fields).length} configurable fields</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleResetModule(moduleKey); }}
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        data-testid={`reset-module-${moduleKey}`}
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />Reset
                      </Button>
                    </div>

                    {/* Module Fields */}
                    {expandedModules[moduleKey] && (
                      <div className="border-t dark:border-slate-700 p-4 space-y-4 bg-slate-50/50 dark:bg-slate-800/50">
                        {Object.entries(moduleData.fields).map(([fieldKey, fieldData]) => (
                          <div key={fieldKey} className="bg-white dark:bg-slate-800 rounded-lg p-4 border dark:border-slate-700">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="font-medium text-slate-900 dark:text-white">{fieldData.label}</h4>
                                <p className="text-xs text-slate-500">{fieldData.description}</p>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setAddOptionDialog({ open: true, module: moduleKey, field: fieldKey });
                                  setNewOption({ value: "", label: "", color: "bg-slate-100 text-slate-700" });
                                }}
                                className="bg-brillien-600 hover:bg-brillien-700 text-white"
                                data-testid={`add-option-${moduleKey}-${fieldKey}`}
                              >
                                <Plus className="h-3 w-3 mr-1" />Add Option
                              </Button>
                            </div>
                            
                            {/* Options List */}
                            <div className="flex flex-wrap gap-2">
                              {(dropdownConfigs[moduleKey]?.[fieldKey] || []).map((option, idx) => (
                                <div 
                                  key={idx}
                                  className="group flex items-center gap-1 px-2 py-1 rounded-full border dark:border-slate-400"
                                >
                                  <Badge className={`${option.color || 'bg-slate-100 text-slate-700'} text-xs`}>
                                    {option.label}
                                  </Badge>
                                  <div className="hidden group-hover:flex items-center gap-0.5 ml-1">
                                    <button
                                      onClick={() => {
                                        setEditOptionDialog({ open: true, module: moduleKey, field: fieldKey, option });
                                        setEditOption({ value: option.value, label: option.label, color: option.color || 'bg-slate-100 text-slate-700' });
                                      }}
                                      className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                                      data-testid={`edit-option-${moduleKey}-${fieldKey}-${option.value}`}
                                    >
                                      <Edit className="h-3 w-3 text-slate-500" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteOption(moduleKey, fieldKey, option.value)}
                                      className="p-0.5 hover:bg-red-100 dark:hover:bg-red-900/50 rounded"
                                      data-testid={`delete-option-${moduleKey}-${fieldKey}-${option.value}`}
                                    >
                                      <Trash2 className="h-3 w-3 text-red-500" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                              {(!dropdownConfigs[moduleKey]?.[fieldKey] || dropdownConfigs[moduleKey][fieldKey].length === 0) && (
                                <p className="text-sm text-slate-400 italic">No options configured. Click "Add Option" to create one.</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>

            {/* Add Option Dialog */}
            <Dialog open={addOptionDialog.open} onOpenChange={(open) => setAddOptionDialog({ ...addOptionDialog, open })}>
              <DialogContent className="max-w-md dark:bg-slate-800 dark:border-slate-700">
                <DialogHeader>
                  <DialogTitle>Add New Option</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Value (used internally) *</Label>
                    <Input
                      value={newOption.value}
                      onChange={(e) => setNewOption({ ...newOption, value: e.target.value })}
                      placeholder="e.g., in_progress"
                      className="dark:border-slate-400 dark:bg-slate-700"
                      data-testid="new-option-value"
                    />
                  </div>
                  <div>
                    <Label>Label (displayed to users) *</Label>
                    <Input
                      value={newOption.label}
                      onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                      placeholder="e.g., In Progress"
                      className="dark:border-slate-400 dark:bg-slate-700"
                      data-testid="new-option-label"
                    />
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Select value={newOption.color} onValueChange={(val) => setNewOption({ ...newOption, color: val })}>
                      <SelectTrigger className="dark:border-slate-400 dark:bg-slate-700" data-testid="new-option-color">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                        {COLOR_OPTIONS.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className="flex items-center gap-2">
                              <Badge className={color.value}>{color.label}</Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleAddOption} className="flex-1 bg-brillien-600 hover:bg-brillien-700" data-testid="save-new-option">
                      Add Option
                    </Button>
                    <Button variant="outline" onClick={() => setAddOptionDialog({ open: false, module: null, field: null })}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Option Dialog */}
            <Dialog open={editOptionDialog.open} onOpenChange={(open) => setEditOptionDialog({ ...editOptionDialog, open })}>
              <DialogContent className="max-w-md dark:bg-slate-800 dark:border-slate-700">
                <DialogHeader>
                  <DialogTitle>Edit Option</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Value (cannot be changed)</Label>
                    <Input
                      value={editOption.value}
                      disabled
                      className="dark:border-slate-400 dark:bg-slate-700 bg-slate-100"
                    />
                  </div>
                  <div>
                    <Label>Label *</Label>
                    <Input
                      value={editOption.label}
                      onChange={(e) => setEditOption({ ...editOption, label: e.target.value })}
                      className="dark:border-slate-400 dark:bg-slate-700"
                      data-testid="edit-option-label"
                    />
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Select value={editOption.color} onValueChange={(val) => setEditOption({ ...editOption, color: val })}>
                      <SelectTrigger className="dark:border-slate-400 dark:bg-slate-700" data-testid="edit-option-color">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                        {COLOR_OPTIONS.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className="flex items-center gap-2">
                              <Badge className={color.value}>{color.label}</Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleEditOption} className="flex-1 bg-brillien-600 hover:bg-brillien-700" data-testid="save-edit-option">
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={() => setEditOptionDialog({ open: false, module: null, field: null, option: null })}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* Storage Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="storage">
            <StorageStats />
          </TabsContent>
        )}

        {/* My Profile Tab */}
        <TabsContent value="profile">
          <div className="space-y-6 max-w-2xl">
            <div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">My Profile</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Manage your personal information and password</p>
            </div>

            {/* Profile Info */}
            <Card className="p-5 border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <User className="h-4 w-4" />Personal Information
              </h3>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input value={profileForm.name} onChange={(e) => setProfileForm({...profileForm, name: e.target.value})} className="dark:border-slate-400 dark:bg-slate-700" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={profileForm.email} disabled className="dark:border-slate-400 dark:bg-slate-700 bg-slate-100" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Role</Label>
                    <Input value={user?.role || ""} disabled className="dark:border-slate-400 dark:bg-slate-700 bg-slate-100" />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <div className="mt-2">
                      <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                    </div>
                  </div>
                </div>
                {user?.role !== "Admin" && user?.assigned_modules?.length > 0 && (
                  <div>
                    <Label className="block mb-2">My Modules</Label>
                    <div className="flex flex-wrap gap-2">
                      {user.assigned_modules.map((mod) => {
                        const moduleInfo = AVAILABLE_MODULES.find(m => m.value === mod);
                        return <Badge key={mod} variant="outline">{moduleInfo?.label || mod}</Badge>;
                      })}
                    </div>
                  </div>
                )}
                <div className="flex justify-end">
                  <Button type="submit" disabled={savingProfile} className="bg-brillien-600 hover:bg-brillien-700">
                    <Save className="h-4 w-4 mr-2" />{savingProfile ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </form>
            </Card>

            {/* Change Password */}
            <Card className="p-5 border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <Lock className="h-4 w-4" />Change Password
              </h3>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label>Current Password</Label>
                  <div className="relative">
                    <Input type={showPasswords.current ? "text" : "password"} value={passwordForm.current_password} onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})} required className="dark:border-slate-400 dark:bg-slate-700 pr-10" />
                    <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1 h-7 w-7 p-0" onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}>
                      {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>New Password</Label>
                    <div className="relative">
                      <Input type={showPasswords.new ? "text" : "password"} value={passwordForm.new_password} onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})} required className="dark:border-slate-400 dark:bg-slate-700 pr-10" />
                      <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1 h-7 w-7 p-0" onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}>
                        {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Confirm New Password</Label>
                    <div className="relative">
                      <Input type={showPasswords.confirm ? "text" : "password"} value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})} required className="dark:border-slate-400 dark:bg-slate-700 pr-10" />
                      <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1 h-7 w-7 p-0" onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}>
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={savingPassword} className="bg-brillien-600 hover:bg-brillien-700">
                    <Lock className="h-4 w-4 mr-2" />{savingPassword ? "Changing..." : "Change Password"}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <div className="space-y-6 max-w-2xl">
            <div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Notification Settings</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Configure how you receive notifications</p>
            </div>

            <Card className="p-5 border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <Bell className="h-4 w-4" />Email Notifications
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b dark:border-slate-700">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Email Notifications</p>
                    <p className="text-sm text-slate-500">Receive notifications via email</p>
                  </div>
                  <Switch checked={notificationSettings.email_notifications} onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, email_notifications: checked})} />
                </div>
                <div className="flex items-center justify-between py-2 border-b dark:border-slate-700">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Lead Alerts</p>
                    <p className="text-sm text-slate-500">Get notified about new leads and updates</p>
                  </div>
                  <Switch checked={notificationSettings.lead_alerts} onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, lead_alerts: checked})} />
                </div>
                <div className="flex items-center justify-between py-2 border-b dark:border-slate-700">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Project Updates</p>
                    <p className="text-sm text-slate-500">Receive project milestone and status updates</p>
                  </div>
                  <Switch checked={notificationSettings.project_updates} onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, project_updates: checked})} />
                </div>
                <div className="flex items-center justify-between py-2 border-b dark:border-slate-700">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Task Reminders</p>
                    <p className="text-sm text-slate-500">Get reminders for upcoming and overdue tasks</p>
                  </div>
                  <Switch checked={notificationSettings.task_reminders} onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, task_reminders: checked})} />
                </div>
                <div className="flex items-center justify-between py-2 border-b dark:border-slate-700">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Payment Alerts</p>
                    <p className="text-sm text-slate-500">Notifications for payment due dates and receipts</p>
                  </div>
                  <Switch checked={notificationSettings.payment_alerts} onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, payment_alerts: checked})} />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Weekly Reports</p>
                    <p className="text-sm text-slate-500">Receive weekly summary reports</p>
                  </div>
                  <Switch checked={notificationSettings.weekly_reports} onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, weekly_reports: checked})} />
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={handleSaveNotificationSettings} disabled={savingNotifications} className="bg-brillien-600 hover:bg-brillien-700">
                  <Save className="h-4 w-4 mr-2" />{savingNotifications ? "Saving..." : "Save Notification Settings"}
                </Button>
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
